import { YieldFetcher } from '../../types'

/**
 * Cap stcUSD — a plain, instant ERC-4626 over cUSD (Cap's stablecoin), on
 * Ethereum. Appreciating share price; the yield is what Cap's whitelisted
 * operators pay to borrow the backing plus its reserve strategies (the API
 * breaks those out as `borrowYieldsUSD` / `investmentYieldsUSD` /
 * `reserveStrategyYieldsUSD`; 56 % of the $98.1M was drawn at assessment).
 *
 * `stakingApr` is a FRACTION (`0.0545` = 5.45 %), so ×100 for the package's
 * percent convention. Averaged over the trailing 7 daily points rather than
 * taken spot: Cap notifies rewards in discrete batches that vest over
 * `lockDuration()` (24 h), so a single day is lumpy.
 *
 * **Both group keys are emitted on purpose.** stcUSD ships at the SAME vanity
 * address on Ethereum, MegaETH (4326) and Katana (747474), but only Ethereum
 * is the vault — the other two are bridged ERC-20 mirrors whose `asset()` /
 * `totalAssets()` / `convertToAssets()` revert — and their `name()` differs in
 * one character: mainnet returns "Staked cap USD", the mirrors "Staked Cap
 * USD". That split the token-list `assetGroup` in two, so a fetcher emitting
 * only the mainnet form reported **0 % intrinsic yield on every market
 * collateralised with the bridged stcUSD** — 21.6 % of the supply sits on
 * MegaETH. A `GROUP_ALIAS` now folds the capitalised form into the mainnet
 * one; the legacy key stays here because a not-yet-republished list still
 * resolves it, exactly as `neutrl.ts` does for sNUSD. One rate covers all
 * three chains — a bridged stcUSD is a claim on the same Ethereum vault, and
 * Cap's API rejects any other `networkId` outright.
 */

const CAP_CUSD_TIMESERIES_URL =
  'https://api.cap.app/v1/vaults/1/0xcCcc62962d17b8914c62D74FfB843d73B2a3cccC/timeseries/1d_1M'

/** Canonical token-list assetGroup — mainnet's `name()`, lowercase `cap`. */
export const STCUSD_KEY = 'Staked cap USD::stcUSD'
/** Pre-alias stored form on the bridged chains (4326 / 747474). */
const STCUSD_BRIDGED_GROUP_KEY = 'Staked Cap USD::stcUSD'

export const capFetcher: YieldFetcher = {
  label: 'CAP',
  fetch: async () => {
    const res = await fetch(CAP_CUSD_TIMESERIES_URL).then((r) => r.json())

    const ts: { stakingApr: string }[] = res?.timeseries ?? []
    const last7 = ts.slice(-7)
    const apr =
      last7.length === 0
        ? 0
        : (last7.reduce((sum, e) => sum + Number(e.stakingApr), 0) /
            last7.length) *
          100

    return { [STCUSD_KEY]: apr, [STCUSD_BRIDGED_GROUP_KEY]: apr }
  },
}
